package meineglasfaser.frontend.common

import com.varabyte.kobweb.core.RouteInfo
import io.ktor.client.*
import io.ktor.client.engine.js.*
import io.ktor.client.plugins.*
import io.ktor.client.plugins.contentnegotiation.*
import io.ktor.client.plugins.resources.*
import io.ktor.client.request.*
import io.ktor.client.statement.*
import io.ktor.http.*
import io.ktor.serialization.kotlinx.json.*
import io.ktor.util.appendAll
import io.ktor.utils.io.CancellationException
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.serialization.json.Json
import meineglasfaser.api.ubl.UBLJsonConfig

object BackendApi {
    val client = HttpClient(Js) {
        expectSuccess = true
        install(ContentNegotiation) {
            json(
                Json(UBLJsonConfig) {
                    ignoreUnknownKeys = true
                }
            )
        }
        install(Resources)
        defaultRequest {
            // TODO make configurable
            //host = "api.meineglasfaser.local.patrickgold.at"
            //host = "api.meineglasfaser.staging.patrickgold.at"
            host = "api.meineglasfaser.info"
            url { protocol = URLProtocol.HTTPS }
        }
    }

    @PublishedApi
    internal val _numActiveRequests = MutableStateFlow(0)
    val numActiveRequests = _numActiveRequests.asStateFlow()

    inline fun safePerformRequest(block: () -> HttpResponse): Result<HttpResponse> {
        _numActiveRequests.update { it + 1 }
        try {
            val response = block()
            return Result.success(response)
        } catch (e: CancellationException) {
            throw e
        } catch (e: Throwable) {
            return Result.failure(e)
        } finally {
            _numActiveRequests.update { it - 1 }
        }
    }

    suspend inline fun <reified T : Any> get(resource: T, block: HttpRequestBuilder.() -> Unit = {}): Result<HttpResponse> {
        return safePerformRequest {
            client.get(resource, block)
        }
    }

    suspend inline fun <reified T : Any> post(resource: T, block: HttpRequestBuilder.() -> Unit = {}): Result<HttpResponse> {
        return safePerformRequest {
            client.post(resource, block)
        }
    }

    suspend inline fun <reified T : Any> put(resource: T, block: HttpRequestBuilder.() -> Unit = {}): Result<HttpResponse> {
        return safePerformRequest {
            client.put(resource, block)
        }
    }

    suspend inline fun <reified T : Any> delete(resource: T, block: HttpRequestBuilder.() -> Unit = {}): Result<HttpResponse> {
        return safePerformRequest {
            client.delete(resource, block)
        }
    }

    suspend inline fun <reified T : Any> options(resource: T, block: HttpRequestBuilder.() -> Unit = {}): Result<HttpResponse> {
        return safePerformRequest {
            client.options(resource, block)
        }
    }

    suspend inline fun <reified T : Any> patch(resource: T, block: HttpRequestBuilder.() -> Unit = {}): Result<HttpResponse> {
        return safePerformRequest {
            client.patch(resource, block)
        }
    }

    suspend inline fun <reified T : Any> head(resource: T, block: HttpRequestBuilder.() -> Unit = {}): Result<HttpResponse> {
        return safePerformRequest {
            client.head(resource, block)
        }
    }
}

inline fun <reified T> HttpRequestBuilder.setJsonBody(data: T) {
    contentType(ContentType.Application.Json)
    setBody(data)
}

fun HttpRequestBuilder.withAuth() {
    fetchOptions {
        credentials = "include"
    }
}

fun RouteInfo.withExistingParams(path: String): String {
    return URLBuilder().run {
        encodedPath = path
        parameters.appendAll(this@withExistingParams.params)
        buildString()
    }.removePrefix(this.origin)
}
